<script setup lang="ts">
// #803: 作品一覧のカテゴリタブ。トップ（`landing/PlaySection.vue`）と
// `/arcanelab` にほぼ同じ markup + CSS が二重で書かれていたのを 1 つにする。
//
// フォントサイズだけは共通化できない —— centering43 の中は `calc(N * var(--u))`、
// landing は `dvw` を使う規約（`doc/architecture/responsive_sizing.md`）があるため。
// 親が `--wg-tab-font-size` を上書きする形で逃がしてある。
import type { WorkGalleryTab } from '~/composables/useWorkGallery'
import type { WorksSortKey } from '~/composables/api/useWorkApi'

interface Props {
  tabs: WorkGalleryTab[]
  modelValue: WorksSortKey
  /**
   * `pill`   = 淡いピルの横並び（トップ / `/arcanelab` デスクトップ）
   * `mobile` = ゆりかごと同サイズ感の大きめピル（`/arcanelab` モバイル。#719）
   */
  variant?: 'pill' | 'mobile'
}

withDefaults(defineProps<Props>(), { variant: 'pill' })

const emit = defineEmits<{ 'update:modelValue': [value: WorksSortKey] }>()
</script>

<template>
  <div
    class="wg-tabs"
    :class="`wg-tabs--${variant}`"
    role="tablist"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      role="tab"
      class="wg-tab"
      :class="{ 'wg-tab--active': modelValue === tab.key }"
      :aria-selected="modelValue === tab.key"
      @click="emit('update:modelValue', tab.key)"
    >
      {{ tab.label }}
    </button>
  </div>
</template>

<style scoped>
.wg-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.wg-tab {
  border: none;
  cursor: pointer;
  font-weight: 700;
  transition: background-color 0.2s, color 0.2s;
}

/* --- pill: トップ / `/arcanelab` デスクトップ --------------------------------- */
.wg-tabs--pill {
  margin-bottom: 12px;
}

.wg-tabs--pill .wg-tab {
  padding: 6px 20px;
  border-radius: 20px;
  /* 親が指定しなければ landing 既定（dvw ベース）。centering43 の中に置く面は
     `--wg-tab-font-size: calc(1.8 * var(--u))` を渡す。 */
  font-size: var(--wg-tab-font-size, clamp(12px, 1.4dvw, 16px));
  background-color: rgba(251, 233, 231, 0.6);
  color: #37474f;
}

.wg-tabs--pill .wg-tab--active {
  background-color: #263238;
  color: #fff;
}

.wg-tabs--pill .wg-tab:hover:not(.wg-tab--active) {
  background-color: rgba(251, 233, 231, 0.9);
}

/* --- mobile: `/arcanelab` モバイル（#719 観点B） ------------------------------ */
.wg-tabs--mobile {
  padding: 4px 0 10px;
  max-width: 560px;
  margin: 0 auto;
}

.wg-tabs--mobile .wg-tab {
  flex: 1 1 0;
  padding: 8px 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 10px;
  background: rgba(60, 40, 40, 0.55);
  color: #fff;
  font-size: 0.95rem;
}

.wg-tabs--mobile .wg-tab--active {
  background: rgba(196, 96, 96, 0.85);
  border-color: #fff;
}
</style>
